import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ElementRef,
    ChangeDetectorRef,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../store';
import {
    generateFlowDeviceCodeAction,
    startPollingCodeStatusAction,
    stopPollingCodeStatusAction,
} from '../../store/flows/flows.actions';
import {
    $deviceCode,
    $deviceCodeLoading,
    $deviceCodeStatus,
} from '../../store/flows/flows.selectors';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DeviceCodeStatus } from '@myrtls/api-interfaces';
import * as QRCode from 'qrcode';

@Component({
    selector: 'myrtls-send-flow-to-device-modal',
    template: `
        <h1 mat-dialog-title>Send Flow to Device</h1>
        @if (
            {
                deviceCode: deviceCode$ | async,
                deviceCodeLoading: deviceCodeLoading$ | async,
                deviceCodeStatus: deviceCodeStatus$ | async,
            };
            as data
        ) {
            <div
                mat-dialog-content
                class="flex flex-col items-center justify-center min-w-[300px] h-[350px]"
            >
                @if (data.deviceCodeLoading) {
                    <mat-spinner [diameter]="40"></mat-spinner>
                    <p class="mt-4">Generating...</p>
                }
                @if (data.deviceCode && !data.deviceCodeLoading) {
                    <div
                        class="relative my-5"
                        [class.expired]="
                            isExpired(data.deviceCodeStatus) || timeLeft === 0
                        "
                        [class.paired]="isPaired(data.deviceCodeStatus)"
                    >
                        <canvas
                            #qrCodeCanvas
                            [ngClass]="{
                                'grayscale opacity-30':
                                    isExpired(data.deviceCodeStatus) ||
                                    timeLeft === 0 ||
                                    isPaired(data.deviceCodeStatus),
                            }"
                        ></canvas>
                        @if (
                            isExpired(data.deviceCodeStatus) || timeLeft === 0
                        ) {
                            <div
                                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl font-bold text-error-300 bg-black bg-opacity-80 px-14 py-2.5 rounded-md"
                            >
                                EXPIRED
                            </div>
                        }
                        @if (isPaired(data.deviceCodeStatus)) {
                            <div
                                class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl font-bold text-success-300 bg-black bg-opacity-80 px-14 py-2.5 rounded-md"
                            >
                                SUCCESSFUL
                            </div>
                        }
                    </div>
                    @if (
                        !isExpired(data.deviceCodeStatus) &&
                        !isPaired(data.deviceCodeStatus) &&
                        timeLeft > 0
                    ) {
                        <p class="text-lg font-bold">
                            Code: {{ data.deviceCode.code }}
                        </p>
                    }
                    @if (timeLeft > 0 && !isPaired(data.deviceCodeStatus)) {
                        <p class="text-sm pt-2">
                            Expires in: {{ timeLeft | date: 'mm:ss' }}
                        </p>
                    }
                }
            </div>
        }
        <div mat-dialog-actions class="flex items-center justify-end">
            <button mat-button (click)="dialogRef.close()">Close</button>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SendFlowToDeviceModalComponent implements OnInit, OnDestroy {
    @ViewChild('qrCodeCanvas', { static: false })
    qrCodeCanvas!: ElementRef<HTMLCanvasElement>;

    private destroy$ = new Subject<void>();
    deviceCode$ = this.store.pipe(select($deviceCode));
    deviceCodeLoading$ = this.store.pipe(select($deviceCodeLoading));
    deviceCodeStatus$ = this.store.pipe(select($deviceCodeStatus));

    timeLeft = 0;
    private timerInterval?: NodeJS.Timeout;

    constructor(
        private readonly store: Store<AppState>,
        public dialogRef: MatDialogRef<void>,
        @Inject(MAT_DIALOG_DATA) public data: { flowId: string },
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.store.dispatch(
            generateFlowDeviceCodeAction({ flowId: this.data.flowId }),
        );

        this.deviceCode$
            .pipe(
                filter(deviceCode => !!deviceCode),
                takeUntil(this.destroy$),
            )
            .subscribe(deviceCode => {
                if (deviceCode) {
                    this.generateQrCode(
                        JSON.stringify({
                            code: deviceCode.code,
                            url: deviceCode.url,
                        }),
                    );
                    this.startTimer(deviceCode.expiresAt);
                    this.store.dispatch(
                        startPollingCodeStatusAction({
                            code: deviceCode.code,
                            intervalTime: 5000,
                        }),
                    );
                }
            });
    }

    ngOnDestroy(): void {
        this.store.dispatch(stopPollingCodeStatusAction());
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    startTimer(expiresAt: number): void {
        const endTime = expiresAt;
        this.timeLeft = Math.max(0, endTime - Date.now());
        this.cdr.markForCheck();

        this.timerInterval = setInterval(() => {
            const now = Date.now();
            this.timeLeft = Math.max(0, endTime - now);
            if (this.timeLeft === 0) {
                clearInterval(this.timerInterval);
            }
            this.cdr.markForCheck();
        }, 1000);
    }

    generateQrCode(code: string): void {
        setTimeout(() => {
            if (this.qrCodeCanvas) {
                QRCode.toCanvas(
                    this.qrCodeCanvas.nativeElement,
                    code,
                    { width: 200 },
                    error => {
                        if (error) console.error(error);
                    },
                );
            }
        }, 0);
    }

    isExpired(status: DeviceCodeStatus | null): boolean {
        return status?.status === 'invalid';
    }

    isPaired(status: DeviceCodeStatus | null): boolean {
        return status?.status === 'used';
    }
}
